import { AlertTriangle, ArrowLeftRight, BadgeCheck, ChevronDown, ChevronRight, Download, FileMinus, FilePlus, Info, PieChart, Receipt, Send } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip } from '@/components/ui/tooltip'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../shared/format'
import { useTaxReport } from './api'
import { ReviewDonut, VatChart } from './components/Charts'
import { Kpi, Money, PeriodSelect, Section, TrendChip } from './components/FinanceBits'
import { TaxCalendarCard, TaxParamsCard } from './components/TaxSideCards'
import { CorporateTab, DocCheckTab, VatRows, WithholdingTab } from './components/TaxTabs'
import { downloadCsv } from './csv'
import { downloadReport } from './reports'
import { FINANCE_OBLIGATION_READ, FINANCE_WRITE_PERM } from './permissions'
import { monthLabel, toastError, useFinanceLookups, usePeriod } from './ui'

const TABS = ['vat', 'withholding', 'corporate', 'docs'] as const
type Tab = (typeof TABS)[number]

const PROFILES = [
  { value: 'as', regime: 'corporate' },
  { value: 'ltd', regime: 'corporate' },
  { value: 'sole', regime: 'income' },
  { value: 'freelance', regime: 'income' },
] as const
type Profile = (typeof PROFILES)[number]['value']
type Regime = (typeof PROFILES)[number]['regime']

function useTaxFilters() {
  const [params, setParams] = useSearchParams()
  const profile = (PROFILES.find((p) => p.value === params.get('profil'))?.value ?? 'as') as Profile
  const regime: Regime = PROFILES.find((p) => p.value === profile)!.regime
  const setProfile = (v: Profile) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        n.set('profil', v)
        return n
      },
      { replace: true },
    )
  const setRegime = (r: Regime) => setProfile(PROFILES.find((p) => p.regime === r)!.value)
  return { profile, regime, setProfile, setRegime }
}

function FilterBox({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-2 rounded-xl border bg-card p-3 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        {hint ? (
          <Tooltip content={<span className="block max-w-64">{hint}</span>} side="top">
            <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={hint}>
              <Info className="size-4" />
            </button>
          </Tooltip>
        ) : null}
      </div>
      {children}
    </div>
  )
}

export function TaxPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { month, setMonth } = usePeriod()
  const { profile, regime, setProfile, setRegime } = useTaxFilters()
  const report = useTaxReport(month)
  const { tx, partyName } = useFinanceLookups()
  const [tab, setTab] = useState<Tab>('vat')
  const r = report.data
  const canWrite = can(FINANCE_WRITE_PERM)

  const exportVat = () => {
    if (!r) return
    downloadCsv(
      `kdv-${month}.csv`,
      [t('finance.tx.date'), t('finance.tx.description'), t('finance.tx.net'), t('finance.tx.vat'), t('finance.tax.vatStatusCol')],
      r.vatRows.flatMap((v) => {
        const x = tx.get(v.transactionId)
        return x ? [[x.date, x.description, x.net, x.vat, t(`finance.vatStatus.${v.vatStatus}`)]] : []
      }),
    )
  }

  const exportWithholding = () => {
    if (!r) return
    downloadCsv(
      `stopaj-${month}.csv`,
      [t('finance.tx.date'), t('finance.tx.description'), t('finance.tax.base'), t('finance.tax.tabs.withholding')],
      r.withholding.rows.flatMap((w) => {
        const x = tx.get(w.transactionId)
        return x ? [[x.date, x.description, w.base, w.amount]] : []
      }),
    )
  }

  const exportDocs = () => {
    const rows = [...tx.values()].filter((x) => x.date.startsWith(month) && (!x.hasDocument || x.review !== 'approved'))
    downloadCsv(
      `belge-kontrolu-${month}.csv`,
      [t('finance.tx.date'), t('finance.tx.description'), t('finance.tax.counterparty'), t('finance.tx.net'), t('finance.tax.docStatus')],
      rows.map((x) => [x.date, x.description, partyName(x.counterpartyId), x.net, x.hasDocument ? t('finance.tax.unreviewed') : t('finance.tax.missingDocs')]),
    )
  }

  const share = () => {
    if (!r) return
    const text = t('finance.tax.shareText', {
      period: monthLabel(month),
      output: fmtMoney(r.vat.output.value),
      input: fmtMoney(r.vat.input.value),
      payable: fmtMoney(r.vat.payable),
      missing: r.docCheck.missing,
    })
    void navigator.clipboard
      .writeText(text)
      .then(() => toast.success(t('finance.tax.shareCopied')))
      .catch(() => toast.error(t('errors.generic')))
  }

  const tabLabel = (k: Tab) => (k === 'corporate' ? t(`finance.tax.tabs.${regime === 'income' ? 'income' : 'corporate'}`) : t(`finance.tax.tabs.${k}`))

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.admin')}
        title={t('finance.tax.title')}
        description={t('finance.tax.subtitle')}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={!r}>
                  <Download />
                  {t('finance.tax.download')}
                  <ChevronDown className="opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                <DropdownMenuLabel>{t('finance.tax.downloadLabel', { period: monthLabel(month) })}</DropdownMenuLabel>
                <DropdownMenuItem onSelect={exportVat}>{t('finance.tax.downloads.vat')}</DropdownMenuItem>
                <DropdownMenuItem onSelect={exportWithholding}>{t('finance.tax.downloads.withholding')}</DropdownMenuItem>
                <DropdownMenuItem onSelect={exportDocs}>{t('finance.tax.downloads.docs')}</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void downloadReport('tax', 'xlsx', { period: month }).catch(toastError)}>
                  {t('finance.reports.excel')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void downloadReport('tax', 'pdf', { period: month }).catch(toastError)}>
                  {t('finance.reports.corporatePdf')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={share} disabled={!r}>
              <Send />
              {t('finance.tax.share')}
            </Button>
          </>
        }
      />

      {report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : !r ? (
        <div className="space-y-4">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/[0.08] p-4 sm:flex-row sm:items-center">
            <AlertTriangle className="size-6 shrink-0 text-warning" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{t('finance.tax.bannerTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('finance.tax.bannerBody')}</p>
            </div>
            {r.docCheck.missing ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/finance/transactions?status=missing">
                  {t('finance.tax.showMissing', { count: r.docCheck.missing })}
                  <ChevronRight />
                </Link>
              </Button>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <FilterBox label={t('finance.period')}>
              <PeriodSelect value={month} onChange={setMonth} className="w-full" />
            </FilterBox>
            <FilterBox label={t('finance.tax.fiscalYear')}>
              <Select
                className="w-full"
                value="2026"
                onChange={() => undefined}
                aria-label={t('finance.tax.fiscalYear')}
                options={[
                  { value: '2026', label: t('finance.tax.fiscalYearRange', { year: 2026 }) },
                  { value: '2025', label: t('finance.tax.fiscalYearRange', { year: 2025 }), description: t('finance.tax.fiscalYearClosed'), disabled: true },
                ]}
              />
            </FilterBox>
            <FilterBox label={t('finance.tax.regime')} hint={t('finance.tax.regimeHint')}>
              <div role="radiogroup" aria-label={t('finance.tax.regime')} className="flex h-9 items-center gap-4">
                {(['corporate', 'income'] as const).map((k) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" name="tax-regime" className="size-4 accent-[var(--color-primary)]" checked={regime === k} onChange={() => setRegime(k)} />
                    {t(`finance.tax.regimes.${k}`)}
                  </label>
                ))}
              </div>
            </FilterBox>
            <FilterBox label={t('finance.tax.profile')}>
              <Select
                className="w-full"
                value={profile}
                onChange={setProfile}
                aria-label={t('finance.tax.profile')}
                options={PROFILES.map((p) => ({ value: p.value, label: t(`finance.tax.profiles.${p.value}`), description: t(`finance.tax.regimes.${p.regime}`) }))}
              />
            </FilterBox>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <Kpi icon={FilePlus} tone="info" label={t('finance.tax.vatOutput')} value={<Money value={r.vat.output.value} />} footer={<TrendChip trend={r.vat.output} />} />
            <Kpi icon={FileMinus} tone="success" label={t('finance.tax.vatInput')} value={<Money value={r.vat.input.value} />} footer={<TrendChip trend={r.vat.input} />} />
            <Kpi icon={ArrowLeftRight} tone="neutral" label={t('finance.tax.vatCarried')} value={<Money value={r.vat.carried} />} footer={t('finance.tax.vatCarriedHint')} />
            <Kpi
              icon={Receipt}
              tone="danger"
              label={t('finance.tax.vatPayable')}
              value={<Money value={r.vat.payable} />}
              footer={Number(r.vat.payable) === 0 && Number(r.vat.carried) > 0 ? t('finance.tax.noPaymentCarried') : t('finance.estimated')}
            />
            <Kpi
              icon={BadgeCheck}
              tone="success"
              label={t('finance.tax.reconciliation')}
              value={`%${r.vat.reconciliationPct}`}
              footer={
                <button type="button" className="font-medium text-primary hover:underline" onClick={() => setTab('docs')}>
                  {t('finance.tax.seeDetails')}
                </button>
              }
            />
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              {TABS.map((k) => (
                <TabsTrigger key={k} value={k}>
                  {tabLabel(k)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="min-w-0 space-y-4">
              {tab === 'vat' ? (
                <>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                    <Section title={t('finance.tax.vatMonthly')}>
                      <VatChart data={r.monthly} />
                    </Section>
                    <Section title={t('finance.tax.reviewTitle')} icon={PieChart}>
                      <ReviewDonut {...r.review} />
                    </Section>
                  </div>
                  <VatRows report={r} />
                </>
              ) : tab === 'withholding' ? (
                <WithholdingTab report={r} />
              ) : tab === 'corporate' ? (
                <CorporateTab report={r} regime={regime} />
              ) : (
                <DocCheckTab report={r} month={month} />
              )}
            </div>
            <aside className="space-y-4">
              <TaxParamsCard canWrite={canWrite} regime={regime} />
              {can(FINANCE_OBLIGATION_READ) ? <TaxCalendarCard /> : null}
              <RelatedLinks />
            </aside>
          </div>
        </div>
      )}
    </>
  )
}

function RelatedLinks() {
  const { t } = useTranslation()
  const links = [
    { to: '/finance/transactions', label: t('nav.financeTransactions') },
    { to: '/finance/e-documents', label: t('nav.financeEDocs') },
    { to: '/finance/parties', label: t('nav.financeParties') },
    { to: '/admin/audit', label: t('nav.audit') },
  ]
  return (
    <Section title={t('finance.tax.related')} bodyClassName="p-2">
      <ul>
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className={cn('flex items-center justify-between rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted')}>
              {l.label}
              <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  )
}
